// src/components/messaging/MessageReactions.tsx
import React from 'react';
import { Box, Chip, Tooltip, alpha } from '@mui/material';
import { MessageReaction } from '../../types/messaging';
import { REACTIONS } from './ReactionPicker';

interface ReactionGroup {
  reaction: string;
  emoji: string;
  count: number;
  users: string[];
  hasOwnReaction: boolean;
}

interface MessageReactionsProps {
  reactions: MessageReaction[];
  currentUserId: number;
  isOwn: boolean;
  onReactionClick?: (reaction: string) => void;
}

const MessageReactions: React.FC<MessageReactionsProps> = ({
  reactions,
  currentUserId,
  isOwn,
  onReactionClick,
}) => {
  if (!reactions || reactions.length === 0) return null;

  // Group reactions by type
  const groupedReactions: ReactionGroup[] = reactions.reduce((acc, r) => {
    const existing = acc.find((g) => g.reaction === r.reaction);
    const reactionDef = REACTIONS.find((rd) => rd.key === r.reaction);
    const emoji = reactionDef?.emoji || r.reaction;
    const userName = r.user?.name || 'Unknown';

    if (existing) {
      existing.count++;
      existing.users.push(userName);
      if (r.user_id === currentUserId) {
        existing.hasOwnReaction = true;
      }
    } else {
      acc.push({
        reaction: r.reaction,
        emoji,
        count: 1,
        users: [userName],
        hasOwnReaction: r.user_id === currentUserId,
      });
    }
    return acc;
  }, [] as ReactionGroup[]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        mt: 0.5,
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        ml: isOwn ? 0 : 1.5,
        mr: isOwn ? 1.5 : 0,
      }}
    >
      {groupedReactions.map(({ reaction, emoji, count, users, hasOwnReaction }) => (
        <Tooltip
          key={reaction}
          title={users.join(', ')}
          arrow
          placement="top"
        >
          <Chip
            size="small"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>{emoji}</span>
                {count > 1 && <span style={{ fontSize: '0.75rem' }}>{count}</span>}
              </Box>
            }
            onClick={() => onReactionClick?.(reaction)}
            sx={{
              height: 24,
              fontSize: '0.85rem',
              backgroundColor: hasOwnReaction
                ? (theme) => alpha(theme.palette.primary.main, 0.2)
                : (theme) => alpha(theme.palette.grey[500], 0.1),
              border: hasOwnReaction ? '1px solid' : 'none',
              borderColor: 'primary.main',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.15),
              },
              '& .MuiChip-label': {
                px: 1,
              },
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
};

export default MessageReactions;
